import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { useLang, fmtPrice } from "@/i18n";
import { useAuth } from "@/AuthContext";
import SiteNav from "@/components/SiteNav";
import { ConsultForm } from "@/components/ConsultModal";
import { BookingDialog } from "@/components/BookingDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Landmark, FileCheck2, Briefcase, MessagesSquare, Video, Users, UsersRound, User, CheckCircle2, Star, Quote, Mail } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1588702547954-4800ead296ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900";
const TEAMS_IMG = "https://images.unsplash.com/photo-1588873281272-14886ba1f737?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900";
const ABOUT_IMG = "/adrienn.jpg";

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay: i * 0.08 },
});

function CountUp({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref} data-testid={`countup-${to}`}>{val.toLocaleString()}{suffix}</span>;
}

function TestimonialCard({ quote, name, result, testid, delay }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const isLong = quote.length > 200;
  return (
    <motion.div {...fade(delay)}>
      <Card className="h-full border-border" data-testid={testid}>
        <CardContent className="p-6">
          <Quote className="w-6 h-6 text-amber-600 mb-3" />
          <p className={`text-stone-700 leading-relaxed ${isLong && !open ? "line-clamp-4" : ""}`} data-testid={`${testid}-quote`}>{quote}</p>
          {isLong && (
            <button onClick={() => setOpen(!open)} className="mt-2 text-sm font-semibold text-primary hover:underline" data-testid={`${testid}-toggle`}>
              {open ? t("results.showLess") : t("results.readMore")}
            </button>
          )}
          <p className="mt-4 font-semibold text-stone-900">{name}</p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-secondary bg-secondary/10 px-2.5 py-1 rounded-full">
            <Star className="w-3.5 h-3.5 text-amber-600" /> {result}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [consultOpen, setConsultOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);

  const goBook = () => {
    if (user) setBookOpen(true);
    else navigate("/login?next=book");
  };

  const specialties = [
    { icon: Landmark, key: "citizenship" },
    { icon: FileCheck2, key: "exams" },
    { icon: Briefcase, key: "business" },
    { icon: MessagesSquare, key: "conversation" },
  ];

  const packages = [
    { id: "single", price: 60, lessons: 1 },
    { id: "pack5", price: 250, displayPrice: 50, lessons: 5, popular: true },
    { id: "pack10", contactOnly: true },
    { id: "group", contactOnly: true, price: 45, lessons: 1, perPerson: true },
    { id: "group2", contactOnly: true, price: 40, lessons: 1, perPerson: true },
    { id: "kids", contactOnly: true, callForPrice: true },
  ];

  const testimonials = ["t1", "t2", "t3", "t4", "t5", "t6", "t7", "t8", "t9", "t10", "t11", "t12", "t13", "t14", "t15", "t16"];
  const [tPage, setTPage] = useState(0);
  const tPages = Math.ceil(testimonials.length / 4);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SiteNav landing />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16 sm:pt-10 sm:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow !text-[13px] !px-6 !py-3 !tracking-normal" data-testid="hero-badge">{t("hero.badge")}</span>
            <h1 className="mt-10 sm:mt-14 text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-stone-900 leading-tight" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-stone-600 leading-relaxed max-w-xl" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="text-base px-7" onClick={() => setConsultOpen(true)} data-testid="hero-book-consult-btn">
                {t("hero.cta")}
              </Button>
              <Button size="lg" variant="outline" className="text-base px-7" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-pricing-btn">
                {t("hero.cta2")}
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-secondary" /> {t("hero.note")}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <div className="absolute -inset-3 bg-amber-200/40 rounded-[2rem] rotate-2" aria-hidden="true" />
            <img src={HERO_IMG} alt="Teacher tutoring online" className="relative rounded-[2rem] shadow-xl w-full aspect-[4/3] object-cover" data-testid="hero-image" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-border px-4 py-3 flex items-center gap-3">
              <Video className="w-6 h-6 text-teal-600" />
              <div>
                <p className="text-sm font-bold">Microsoft Teams</p>
                <p className="text-xs text-muted-foreground">{t("levels.types.online")}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOOK */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div {...fade()} className="rounded-2xl border-2 border-amber-400 bg-amber-50/70 px-6 py-8 sm:px-10 text-center shadow-sm" data-testid="hook-box">
            <p className="text-lg sm:text-xl font-serif font-semibold text-stone-800 leading-relaxed" data-testid="hook-q1">{t("hook.q1")}</p>
            <p className="mt-3 text-lg sm:text-xl font-serif font-semibold text-stone-800 leading-relaxed" data-testid="hook-q2">{t("hook.q2")}</p>
            <p className="mt-3 text-lg sm:text-xl font-serif font-semibold text-stone-800 leading-relaxed" data-testid="hook-q3">{t("hook.q3")}</p>
          </motion.div>
        </div>
      </section>

      {/* TRUST METRICS */}
      <section className="bg-secondary text-secondary-foreground py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            [32, "+", t("trust.years")],
            [1400, "+", t("trust.students")],
            [300, "+", t("trust.pass")],
            [100, "%", t("trust.personal")],
          ].map(([num, suffix, label], i) => (
            <motion.div key={label} {...fade(i)}>
              <p className="text-3xl sm:text-4xl font-serif font-bold text-amber-300"><CountUp to={num} suffix={suffix} /></p>
              <p className="mt-1 text-sm text-secondary-foreground/80">{label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade()} className="order-2 lg:order-1">
            <img src={ABOUT_IMG} alt="Your teacher" className="rounded-[2rem] shadow-xl w-full aspect-[4/3] object-cover" data-testid="about-image" />
          </motion.div>
          <motion.div {...fade(1)} className="order-1 lg:order-2">
            <span className="eyebrow">{t("about.eyebrow")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900">{t("about.heading")}</h2>
            <p className="mt-4 text-stone-600 leading-relaxed" data-testid="about-text">{t("about.p1")}</p>
            <p className="mt-3 text-stone-600 leading-relaxed" data-testid="about-text-2">{t("about.p2")}</p>
            <p className="mt-3 text-stone-600 leading-relaxed" data-testid="about-text-3">{t("about.p3")}</p>
            <p className="mt-3 text-stone-600 leading-relaxed" data-testid="about-text-4">{t("about.p4")}</p>
          </motion.div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-16 sm:mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade(1)}>
            <span className="eyebrow">{t("about.coEyebrow")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900" data-testid="coteacher-name">{t("about.coName")}</h2>
            <p className="mt-4 text-stone-600 leading-relaxed" data-testid="coteacher-text">{t("about.coP1")}</p>
          </motion.div>
          <motion.div {...fade()}>
            <img src="/heather.jpg" alt="Co-teacher Heather" className="rounded-[2rem] shadow-xl w-full aspect-[4/3] object-cover object-top" data-testid="coteacher-image" />
          </motion.div>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section id="specialties" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fade()} className="max-w-2xl">
            <span className="eyebrow">{t("specialties.eyebrow")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900">{t("specialties.heading")}</h2>
            <p className="mt-3 text-stone-600">{t("specialties.sub")}</p>
          </motion.div>
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {specialties.map(({ icon: Icon, key }, i) => (
              <motion.div key={key} {...fade(i)}>
                <Card className="h-full border-border hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`specialty-card-${key}`}>
                  <CardContent className="p-7">
                    <span className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </span>
                    <h3 className="mt-4 text-xl font-serif font-semibold text-stone-800">{t(`specialties.${key}.t`)}</h3>
                    <p className="mt-2 text-stone-600 leading-relaxed">{t(`specialties.${key}.d`)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LEVELS & FORMATS */}
      <section id="levels" className="py-20 sm:py-28 bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade()}>
            <span className="eyebrow">{t("levels.eyebrow")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900">{t("levels.heading")}</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {["beginner", "elementary", "intermediate", "upper_intermediate", "advanced", "proficient"].map((lv) => (
                <Badge key={lv} variant="secondary" className="text-sm px-3 py-1.5" data-testid={`level-badge-${lv}`}>
                  {t(`levels.items.${lv}`)}
                </Badge>
              ))}
            </div>
            <h3 className="mt-8 text-lg font-serif font-semibold text-stone-800">{t("levels.typesHeading")}</h3>
            <ul className="mt-3 space-y-2.5">
              {[
                [User, t("levels.types.private")],
                [Users, t("levels.types.pair")],
                [UsersRound, t("levels.types.group")],
                [Video, t("levels.types.online")],
              ].map(([Icon, label], i) => (
                <li key={i} className="flex items-center gap-3 text-stone-700">
                  <Icon className="w-5 h-5 text-primary shrink-0" /> {label}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-muted-foreground italic">{t("levels.online_note")}</p>
          </motion.div>
          <motion.div {...fade(1)}>
            <img src={TEAMS_IMG} alt="Online lesson via Teams" className="rounded-[2rem] shadow-xl w-full aspect-[4/3] object-cover" data-testid="levels-image" />
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fade()} className="max-w-2xl mx-auto text-center">
            <span className="eyebrow">{t("pricing.eyebrow")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900">{t("pricing.heading")}</h2>
            <p className="mt-3 text-stone-600">{t("pricing.sub")}</p>
          </motion.div>
          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            {packages.map((p, i) => (
              <motion.div key={p.id} {...fade(i)}>
                <Card className={`relative h-full ${p.popular ? "border-primary border-2 shadow-lg" : "border-border"}`} data-testid={`package-card-${p.id}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                      {t("pricing.popular")}
                    </span>
                  )}
                  <CardContent className="p-7 text-center flex flex-col h-full">
                    <h3 className="text-xl font-serif font-semibold">{t(`pricing.${p.id}.name`)}</h3>
                    {p.price != null ? (
                      <>
                        <p className="mt-4 text-4xl font-serif font-bold text-primary">{fmtPrice(p.displayPrice ?? p.price, lang)}</p>
                        <p className="text-sm text-muted-foreground">{fmtPrice(Math.round(p.price / p.lessons), lang)} {t(p.perPerson ? "pricing.perLessonPerson" : "pricing.perLesson")}</p>
                      </>
                    ) : p.callForPrice ? (
                      <p className="mt-4 text-2xl font-serif font-bold text-primary" data-testid={`package-callprice-${p.id}`}>{t("pricing.callForPrice")}</p>
                    ) : null}
                    <p className="mt-4 text-stone-600 flex-1">{t(`pricing.${p.id}.d`)}</p>
                    <Button className="mt-6 w-full" variant={p.popular ? "default" : "outline"} onClick={p.contactOnly ? () => setConsultOpen(true) : goBook} data-testid={`package-book-${p.id}`}>
                      {p.contactOnly ? t("pricing.contactCta") : t("pricing.book")}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / ZELLE */}
      <section id="how" className="py-20 sm:py-28 bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fade()} className="max-w-2xl">
            <span className="eyebrow">{t("zelle.eyebrow")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900">{t("zelle.heading")}</h2>
          </motion.div>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {["s1", "s2", "s3"].map((s, i) => (
              <motion.div key={s} {...fade(i)} className="relative">
                <span className="text-5xl font-serif font-bold text-amber-600/25">{i + 1}</span>
                <h3 className="mt-2 text-lg font-serif font-semibold text-stone-800">{t(`zelle.${s}.t`)}</h3>
                <p className="mt-2 text-stone-600">{t(`zelle.${s}.d`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="results" className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <motion.div {...fade()}>
              <span className="eyebrow">{t("results.eyebrow")}</span>
              <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-stone-900">{t("results.heading")}</h2>
              <img src="/logo.png" alt="English Universe Stars logo" className="mt-8 rounded-2xl shadow-lg w-full aspect-[2/1] object-cover hidden lg:block" data-testid="results-logo" />
            </motion.div>
            <div className="lg:col-span-2">
              <div className="grid sm:grid-cols-2 gap-6">
              {testimonials.slice(tPage * 4, tPage * 4 + 4).map((key, i) => (
                <TestimonialCard
                  key={`${tPage}-${key}`}
                  delay={i}
                  testid={`testimonial-${key}`}
                  quote={t(`results.${key}.q`)}
                  name={t(`results.${key}.n`)}
                  result={t(`results.${key}.r`)}
                />
              ))}
              </div>
              <div className="mt-8 flex items-center justify-center gap-4" data-testid="testimonial-pagination">
                <Button variant="outline" size="sm" disabled={tPage === 0} onClick={() => setTPage(tPage - 1)} data-testid="testimonials-prev-btn">{t("results.prev")}</Button>
                <span className="text-sm font-semibold text-muted-foreground" data-testid="testimonials-page-indicator">{tPage + 1} / {tPages}</span>
                <Button variant="outline" size="sm" disabled={tPage === tPages - 1} onClick={() => setTPage(tPage + 1)} data-testid="testimonials-next-btn">{t("results.next")}</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 sm:py-28 bg-secondary text-secondary-foreground">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-start">
          <motion.div {...fade()}>
            <span className="eyebrow">{t("nav.book")}</span>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight">{t("contact.heading")}</h2>
            <p className="mt-3 text-secondary-foreground/80 max-w-md">{t("contact.sub")}</p>
            <div className="mt-8 flex items-center gap-3 text-sm">
              <Mail className="w-5 h-5 text-amber-300" />
              <span>englishuniversestars@yahoo.com</span>
            </div>
          </motion.div>
          <motion.div {...fade(1)}>
            <Card className="border-border" data-testid="contact-form-card">
              <CardContent className="p-7">
                <ConsultForm />
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-serif font-bold">English Universe Stars</p>
          <p className="text-sm text-muted-foreground">{t("footer.tagline")}</p>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} {t("footer.rights")}</p>
        </div>
      </footer>

      {consultOpen && (
        <ConsultModalWrapper open={consultOpen} onOpenChange={setConsultOpen} />
      )}
      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} />
    </div>
  );
}

import ConsultModal from "@/components/ConsultModal";
function ConsultModalWrapper(props) {
  return <ConsultModal {...props} />;
}
